// Banner shown across the dashboard while the user's Amazon catalogue is
// syncing — or when it's failed and needs reconnecting. Disappears when the
// status flips to `complete`.

import React, { useContext, useState } from 'react';
import { useAmazonSync } from '../../context/AmazonSyncContext';
import AuthContext from '../../context/AuthContext';
import { AMAZON_MAINTENANCE_MODE } from '../../config/amazonMaintenance';
import { useNotification } from '../../context/NotificationContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AmazonSyncBanner = () => {
    const { status, error, refresh } = useAmazonSync();
    const authCtx = useContext(AuthContext);
    const { showAmazonMaintenanceMessage } = useNotification();
    const [reconnecting, setReconnecting] = useState(false);

    const handleReconnect = async () => {
        if (AMAZON_MAINTENANCE_MODE) {
            showAmazonMaintenanceMessage();
            return;
        }
        if (reconnecting || !authCtx.token) return;
        setReconnecting(true);
        try {
            const res = await fetch(`${API_URL}/auth/amazon/login`, {
                headers: { Authorization: `Bearer ${authCtx.token}` },
            });
            const data = await res.json();
            if (data.auth_url) {
                window.location.href = data.auth_url;
                return;
            }
        } catch (e) {
            // ignored; user can retry
        }
        setReconnecting(false);
    };

    if (status === 'syncing') {
        return (
            <div className="w-full px-5 py-3 bg-[#EEF5FF] border-b border-[#D0E7FF] flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <svg className="animate-spin w-5 h-5 text-[#2766AA]" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <div className="flex flex-col">
                        <span className="text-[#2766AA] text-sm font-semibold font-sans">Fetching your Amazon catalogue…</span>
                        <span className="text-[#505050] text-xs font-medium font-sans">
                            Usually 5–10 minutes. You can paste any ASIN to keep working in the meantime. New listings are paused until this finishes.
                        </span>
                    </div>
                </div>
                <button
                    onClick={refresh}
                    className="text-[#2766AA] text-xs font-semibold hover:underline bg-transparent border-none cursor-pointer"
                >
                    Check now
                </button>
            </div>
        );
    }

    if (status === 'failed') {
        return (
            <div className="w-full px-5 py-3 bg-[#FFF4F4] border-b border-[#FFD3D3] flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#D33A3A]" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 7v6M12 16v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <div className="flex flex-col">
                        <span className="text-[#B82424] text-sm font-semibold font-sans">We couldn't finish syncing your Amazon catalogue.</span>
                        <span className="text-[#505050] text-xs font-medium font-sans">
                            {error || 'Something went wrong. Reconnect your Amazon account to try again.'}
                        </span>
                    </div>
                </div>
                <button
                    onClick={handleReconnect}
                    disabled={reconnecting}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#2766AA] to-[#0752B0] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                    {reconnecting ? 'Connecting…' : 'Reconnect Amazon'}
                </button>
            </div>
        );
    }

    return null;
};

export default AmazonSyncBanner;
